import { Link } from "react-router-dom";
import { FileText, Camera, Film, ArrowRight } from "lucide-react";
import MarketingShell from "@/components/marketing/MarketingShell";

const SERVICES = [
    {
        id: "campaign",
        icon: FileText,
        title: "Campaign Production",
        status: "Live",
        body: "Complete brand strategy, research, concepts, scripts, storyboards, and presentation-ready outputs.",
        bullets: [
            "Brand DNA + verified-fact research",
            "3 script variants per concept",
            "Storyboard with shot list, timing, visual cues",
            "Client-ready PDFs, presentation decks, share links",
        ],
    },
    {
        id: "catalog",
        icon: Camera,
        title: "Catalog & Product Visuals",
        status: "Expansion",
        body: "AI-assisted product concepts, styling directions, and catalog visual planning for fashion, jewelry, and product brands.",
        bullets: [
            "Product styling moodboards",
            "Catalog layout direction",
            "Per-SKU visual brief",
            "Photographer / shoot prep packs",
        ],
    },
    {
        id: "film",
        icon: Film,
        title: "Film & Story Development",
        status: "Expansion",
        body: "Story ideation, screenplay structuring, cinematic breakdowns, and storyboard generation.",
        bullets: [
            "Logline + treatment generation",
            "Beat-by-beat outlining",
            "Cinematic shot breakdowns",
            "Storyboard frames per scene",
        ],
    },
];

export default function Services() {
    return (
        <MarketingShell
            title="Services — Furmais | Campaign, Catalog, Film"
            description="Furmais services: AI-led Campaign Production, Catalog & Product Visuals, and Film & Story Development. One operating system, three production modes."
            ogTitle="Furmais Services"
            ogDescription="Campaign, Catalog, and Film production — orchestrated by AI."
        >
            <section className="relative z-10 px-6 sm:px-10 pt-12 pb-12 max-w-5xl mx-auto" data-testid="services-page">
                <div className="label-micro mb-3 text-[#FF4400]">Services</div>
                <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-4">Three modes. One operating system.</h1>
                <p className="text-zinc-400 text-base max-w-2xl">
                    Furmais runs three production tracks. Campaign Production is live today.
                    Catalog and Film modes are in expansion — built on the same agent architecture.
                </p>
            </section>

            <section className="relative z-10 px-6 sm:px-10 pb-20 max-w-5xl mx-auto space-y-5">
                {SERVICES.map(s => (
                    <div key={s.id} data-testid={`service-card-${s.id}`}
                        className="border border-zinc-900 bg-[#080808] rounded-sm p-6 hover:border-[#FF4400]/40 transition-colors">
                        <div className="flex items-start justify-between mb-3 gap-3">
                            <div className="flex items-center gap-3">
                                <s.icon className="w-6 h-6 text-[#FF4400]" />
                                <h2 className="font-display text-2xl tracking-tight">{s.title}</h2>
                            </div>
                            <span className={`shrink-0 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border ${s.status === "Live" ? "border-emerald-500/40 text-emerald-400" : "border-amber-500/40 text-amber-400"}`}>
                                {s.status === "Live" ? "Live" : "Expansion · Coming Soon"}
                            </span>
                        </div>
                        <p className="text-zinc-300 leading-relaxed mb-4">{s.body}</p>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {s.bullets.map((b, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-zinc-400">
                                    <span className="w-1 h-1 rounded-full bg-[#FF4400] mt-2 shrink-0" /> {b}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </section>

            <section className="relative z-10 px-6 sm:px-10 pb-24 max-w-5xl mx-auto">
                <div className="border border-zinc-900 bg-[#080808] rounded-sm p-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-5">
                    <div>
                        <div className="label-micro mb-2">Get started</div>
                        <h3 className="font-display text-2xl sm:text-3xl tracking-tight">Brief in. Production out.</h3>
                    </div>
                    <Link to="/login" data-testid="services-cta-start" className="btn-accent text-sm">
                        Start a Project <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
        </MarketingShell>
    );
}
